import React, { useEffect, useRef, useState } from "react";
import { motion, useInView, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { FONTS } from "./landingTheme";
import { LogoIcon } from "./LogoIcons";

/* ─────────────────────── CARD STACK ANIMATION VARIANTS ─────────────────────── */
const cardVariants = {
    stacked: ({ index, offset }) => {
        // Empilha tudo no centro (usando o card 1 como âncora)
        let xPos = 0;
        if (index === 0) xPos = offset;     // Card da esquerda vai para a direita (centro)
        if (index === 2) xPos = -offset;    // Card da direita vai para a esquerda (centro)

        // Card do centro fica normal, laterais ficam menores e levemente transparentes
        const scalePos = index === 1 ? 1 : 0.95;
        const opacityPos = index === 1 ? 1 : 0.6;
        const zIndexPos = index === 1 ? 10 : 5; // O do centro sempre fica por cima

        return {
            x: xPos,
            scale: scalePos,
            opacity: opacityPos,
            zIndex: zIndexPos,
            transition: {
                type: "spring",
                stiffness: 100,
                damping: 14,
                delay: index * 0.05
            }
        };
    },
    spread: ({ index }) => ({
        x: 0,
        scale: 1,
        opacity: 1,
        zIndex: index === 1 ? 10 : 5,
        transition: {
            type: "spring",
            stiffness: 100,
            damping: 14,
            delay: index * 0.06
        }
    })
};

/* ─────────────────────── FEATURE SECTION ─────────────────────── */
const FeatureSection = ({ colors, title, logoColor, sectionLabel, sectionIcon, cards }) => {
    const sectionRef = useRef(null);
    const titleRef = useRef(null);
    const cardsRef = useRef(null);
    const [windowWidth, setWindowWidth] = useState(typeof document !== "undefined" ? document.documentElement.clientWidth : 1300);

    useEffect(() => {
        const handleResize = () => setWindowWidth(document.documentElement.clientWidth);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // ──────────────── CONFIGURAÇÃO DOS CARDS ────────────────
    // Celular: textos e espaçamentos menores
    const isMobile = windowWidth < 768;
    // Até ~1140px os 3 cards (340px + padding que vaza 20%) não cabem no tamanho cheio: ficam lado a lado, menores
    const compact = windowWidth < 1140;
    const compactGap = isMobile ? 8 : 16;
    const cardWidth = compact ? Math.floor((windowWidth - (isMobile ? 24 : 48) - 2 * compactGap) / 3) : 340;
    const cardHeight = compact ? Math.round(cardWidth * 1.45) : isMobile ? 420 : 500;

    // ──────────────── CONFIGURAÇÃO DA ANIMAÇÃO DE SEPARAÇÃO ────────────────
    // Para resolver o problema de abrir no momento errado, ajustamos a forma como o elemento é medido.
    // Agora você pode usar estas duas variáveis para ter controle total:
    //
    // 1. CARDS_VISIBLE_PERCENTAGE: Quanto dos cards precisa estar visível na tela para permitir a abertura?
    //    Use um número de 0 a 1 (Ex: 1 = 100% visível, 0.5 = 50% visível).
    //    Se a tela for muito pequena, 1 pode não ativar. 0.8 ou 0.9 é mais seguro.
    const CARDS_VISIBLE_PERCENTAGE = 0.8;

    // 2. CARDS_SCROLL_EXTRA_OFFSET: Depois de atingir a porcentagem acima,
    //    quanto você quer rolar a tela antes de abrir?
    //    - Valores negativos (ex: "-150px") = você precisa rolar MAIS para baixo para abrir.
    //    - Valores positivos (ex: "50px") = abre ANTES de atingir a linha.
    //    - "0px" = abre exatamente quando atinge a porcentagem.
    const CARDS_SCROLL_EXTRA_OFFSET = "-100px";
    // ────────────────────────────────────────────────────────

    // 1. Limite máximo do container (igual a navbar)
    const containerWidth = Math.min(windowWidth - 48, 1232);

    // 2. Espaçamento automático: Pega o tamanho total (ex: 1300), subtrai o tamanho dos 3 cards,
    // e divide o espaço que sobrar nos 2 "buracos" entre eles.
    const gap = compact ? compactGap : Math.max(10, (containerWidth - (3 * cardWidth)) / 2);

    // 3. Calculo da animação de empilhamento no centro
    const peekEdge = compact ? Math.round(cardWidth * 0.15) : 48; // Borda que fica visível dos cards de trás quando estão empilhados
    const offset = cardWidth + gap - peekEdge;

    const { scrollYProgress } = useScroll({
        target: sectionRef, // Target the whole section to track from the moment it enters
        offset: ["start end", "start center"], // From bottom of screen to center
    });

    // Parallax effect: moves down as the page scrolls up, creating a "sticky reveal" illusion
    const titleY = useTransform(scrollYProgress, [0, 1], [-350, 0]);
    const opacity = useTransform(scrollYProgress, [isMobile ? 0.4 : 0.2, 1], [0, 1]);
    const scale = useTransform(scrollYProgress, [0, 1], [0.85, 1]);

    const cardsInView = useInView(cardsRef, {
        amount: CARDS_VISIBLE_PERCENTAGE,
        margin: `0px 0px ${CARDS_SCROLL_EXTRA_OFFSET} 0px`
    });

    return (
        <section ref={sectionRef} style={{ padding: isMobile ? "40px 0 80px" : "60px 0 220px", position: "relative", zIndex: 1 }}>
            <div style={{ maxWidth: 1300, margin: "0 auto", padding: isMobile ? "0 12px" : "0 24px" }}>
                <motion.div
                    ref={titleRef}
                    style={{
                        display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center",
                        marginBottom: isMobile ? 48 : 100, opacity, scale, y: titleY, transformOrigin: "center center"
                    }}
                >
                    <h2 style={{
                        fontFamily: `'CustomHeroFont', ${FONTS.heading}`,
                        fontSize: isMobile ? "clamp(18px, 4.6vw, 40px)" : "clamp(40px, 6vw, 70px)", // Deixei maior, igual ao topo
                        fontWeight: 800,
                        color: colors.textDark,
                        lineHeight: 1.1,
                        letterSpacing: isMobile ? 0 : 2, // Mudei de -2 para 2 para ficar mais espaçado
                        margin: "0 0 32px",
                        maxWidth: 1300
                    }}>
                        {typeof title === "string" ? title.split("\n").map((line, idx) => (
                            <React.Fragment key={idx}>
                                {idx > 0 && <br />}
                                {line.split("👻").map((part, i) =>
                                    i > 0 ? (
                                        <React.Fragment key={i}>
                                            <LogoIcon size={60} color={logoColor} style={{ verticalAlign: "middle", margin: "0 12px" }} />
                                            {part}
                                        </React.Fragment>
                                    ) : part
                                )}
                            </React.Fragment>
                        )) : title}
                    </h2>
                </motion.div>

                {sectionLabel && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={cardsInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                        transition={{ duration: 0.5 }}
                        style={{ display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 32 }}
                    >
                        <div style={{ display: "flex", alignItems: "center", gap: 12, fontFamily: FONTS.body, fontSize: 18, fontWeight: 700, color: colors.textDark }}>
                            {sectionIcon} {sectionLabel}
                        </div>
                    </motion.div>
                )}
            </div>

            <motion.div
                ref={cardsRef}
                initial="stacked"
                animate={cardsInView ? "spread" : "stacked"}
                style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap,
                    width: `calc(100% - ${isMobile ? 24 : 48}px)`,
                    maxWidth: 1300,
                    margin: "0 auto",
                    paddingTop: compact ? 0 : 40,
                }}
            >
                {cards.slice(0, 3).map((card, i) => (
                    <FeatureCard
                        key={i}
                        index={i}
                        offset={offset}
                        cardWidth={cardWidth}
                        cardHeight={cardHeight}
                        compact={compact}
                        isMobile={isMobile}
                        {...card}
                    />
                ))}
            </motion.div>
        </section>
    );
};

/* ─────────────────────── GLOBAL VIDEO SYNC MANAGER ─────────────────────── */
const globalSyncManager = {
    videos: new Set(),
    readyVideos: new Set(),
    hasStarted: false,
    startFallbackTimeout: null,
    register(videoEl) {
        if (!videoEl) return () => { };

        this.videos.add(videoEl);

        const startAll = () => {
            if (!this.hasStarted) {
                this.hasStarted = true;
                this.videos.forEach(v => {
                    v.play().catch(() => { });
                });
            }
        };

        const checkAllReady = () => {
            this.readyVideos.add(videoEl);
            if (this.readyVideos.size >= this.videos.size && this.videos.size > 0) {
                startAll();
            }
        };

        const handleCanPlay = () => checkAllReady();

        videoEl.addEventListener("canplaythrough", handleCanPlay);
        videoEl.addEventListener("canplay", handleCanPlay);
        videoEl.addEventListener("loadeddata", handleCanPlay);

        if (videoEl.readyState >= 2) {
            checkAllReady();
        }

        // Fallback: force play after 800ms if any video takes longer to emit load events
        if (!this.startFallbackTimeout) {
            this.startFallbackTimeout = setTimeout(() => {
                startAll();
            }, 800);
        }

        return () => {
            videoEl.removeEventListener("canplaythrough", handleCanPlay);
            videoEl.removeEventListener("canplay", handleCanPlay);
            videoEl.removeEventListener("loadeddata", handleCanPlay);
            this.videos.delete(videoEl);
            this.readyVideos.delete(videoEl);
            if (this.videos.size === 0) {
                this.hasStarted = false;
                if (this.startFallbackTimeout) {
                    clearTimeout(this.startFallbackTimeout);
                    this.startFallbackTimeout = null;
                }
            }
        };
    }
};

const SequentialVideoPlayer = ({ videos }) => {
    const [currentVideoIndex, setCurrentVideoIndex] = React.useState(0);
    const videoRefs = React.useRef([]);

    React.useEffect(() => {
        const currentVideoEl = videoRefs.current[currentVideoIndex];
        if (currentVideoEl) {
            return globalSyncManager.register(currentVideoEl);
        }
    }, [currentVideoIndex]);

    const handleEnded = (index) => {
        const nextIndex = (index + 1) % videos.length;
        setCurrentVideoIndex(nextIndex);
        if (videoRefs.current[nextIndex]) {
            videoRefs.current[nextIndex].currentTime = 0;
            videoRefs.current[nextIndex].play().catch(() => { });
        }
    };

    if (!videos || videos.length === 0) return null;

    return (
        <div style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 0,
            borderRadius: "inherit", // acompanha o raio do card (32 no desktop, 20 no celular)
            backgroundColor: "#000",
            overflow: "hidden"
        }}>
            {videos.map((src, index) => (
                <video
                    key={index}
                    ref={(el) => (videoRefs.current[index] = el)}
                    src={src}
                    loop={videos.length === 1}
                    autoPlay
                    muted
                    playsInline
                    onEnded={videos.length === 1 ? undefined : () => handleEnded(index)}
                    style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        opacity: currentVideoIndex === index ? 1 : 0,
                        transition: videos.length === 1 ? "none" : "opacity 0.2s ease-in-out"
                    }}
                />
            ))}
        </div>
    );
};

const FeatureCard = ({ text, bgColor, index, offset, cardWidth, cardHeight, videos, icon, compact, isMobile }) => {
    // Independent motion values for hover repulsion
    const x = useMotionValue(0);
    const y = useMotionValue(0);

    const springConfig = { stiffness: 200, damping: 12, mass: 2 };
    const smoothX = useSpring(x, springConfig);
    const smoothY = useSpring(y, springConfig);

    const handleMouseMove = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        const offsetX = e.clientX - centerX;
        const offsetY = e.clientY - centerY;

        const pushFactor = 0.08;

        x.set(-offsetX * pushFactor);
        y.set(-12 + (-offsetY * pushFactor));
    };

    const handleMouseEnter = (e) => {
        e.currentTarget.style.boxShadow = "0 24px 48px rgba(0,0,0,0.12)";
    };

    const handleMouseLeave = (e) => {
        x.set(0);
        y.set(0);
        e.currentTarget.style.boxShadow = "0 10px 30px rgba(0,0,0,0.08)";
    };

    return (
        <motion.div
            custom={{ index, offset }}
            variants={cardVariants}
            style={{
                width: cardWidth,
                minWidth: cardWidth,
                height: cardHeight,
                minHeight: cardHeight,
                flexShrink: 0,
                position: "relative"
            }}
        >
            <motion.div
                style={{
                    x: smoothX,
                    y: smoothY,
                    width: "100%", height: "100%",
                    // Compacto: o padding de 10% fica dentro do card (no tamanho cheio ele cresce 20% e o gap entre os cards absorve)
                    boxSizing: compact ? "border-box" : undefined,
                    borderRadius: compact ? 20 : 32, background: bgColor,
                    padding: "10%", // Responsive padding instead of fixed 40px
                    display: "flex", flexDirection: "column", justifyContent: "space-between",
                    cursor: "pointer", position: "relative", overflow: "hidden",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
                    transformOrigin: "center center"
                }}
                onMouseEnter={handleMouseEnter}
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
            >
                <SequentialVideoPlayer videos={videos} />
                {icon ? (
                    <div style={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        width: "100%",
                        backgroundColor: "rgba(25, 25, 25, 0.95)",
                        padding: isMobile ? "10px 6px" : compact ? "16px" : "24px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: isMobile ? "center" : undefined,
                        gap: compact ? "10px" : "16px",
                        borderBottomLeftRadius: compact ? 20 : 32,
                        borderBottomRightRadius: compact ? 20 : 32,
                        zIndex: 2,
                        boxSizing: "border-box"
                    }}>
                        {!isMobile && <div style={{ color: "#FFF", display: "flex", flexShrink: 0, alignItems: "center", transform: compact ? "scale(0.75)" : undefined }}>{icon}</div>}
                        <div style={{
                            fontFamily: "'BoldFont', sans-serif",
                            fontSize: isMobile ? "clamp(10px, 3.2vw, 14px)" : compact ? "20px" : "28px",
                            fontWeight: 900,
                            color: "#FFF",
                            textTransform: "uppercase",
                            textAlign: "left",
                            lineHeight: 1.1
                        }}>
                            {text}
                        </div>
                    </div>
                ) : (
                    <p style={{
                        fontFamily: "'Inter', sans-serif",
                        fontSize: "clamp(24px, 2.5vw, 36px)",
                        fontWeight: 900, color: "#FFFFFF", lineHeight: 1.2,
                        margin: "auto",
                        textAlign: "center", width: "100%",
                        display: "block",
                        position: "relative", zIndex: 2, textTransform: "uppercase"
                    }}>
                        {text}
                    </p>
                )}
                <div style={{
                    position: "absolute", bottom: 0, right: 0, width: "80%", height: "70%",
                    background: `linear-gradient(135deg, transparent 20%, rgba(255,255,255,0.1) 100%)`,
                    borderTopLeftRadius: 100, zIndex: 1,
                }} />
            </motion.div>
        </motion.div>
    );
};

export default FeatureSection;

